import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import TextArea from "common/components/ui/TextArea";
import useLeadNotes from "common/datahooks/useLeadNotes";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { NoteSchema, type NoteSchemaType } from "common/schemas";
import type { Lead } from "common/types";

import { startLeadsBulkAction } from "../../../utils";

interface AddNotesProps {
  selectedLeads: Lead[];
  onBack?: () => void;
  onClose: () => void;
}

export default function AddNotes({
  selectedLeads,
  onBack,
  onClose,
}: AddNotesProps) {
  const isDesktop = useTwBreakpoint("lg");

  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const { addLeadNote } = useLeadNotes();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NoteSchemaType>({
    resolver: valibotResolver(NoteSchema),
  });

  const title = "Add notes";
  const description =
    selectedLeads.length === 1
      ? `Add a note to ${selectedLeads[0].full_name}`
      : `Add a note to ${selectedLeads.length} leads`;

  function onSubmit({ text }: NoteSchemaType) {
    startLeadsBulkAction(
      (lead, abortSignal) =>
        addLeadNote({ profileId: lead.id, text, abortSignal }),
      selectedLeads,
      (finishedItemsCount) =>
        finishedItemsCount === selectedLeads.length
          ? "Notes added"
          : `Added notes to ${finishedItemsCount}/${selectedLeads.length} leads`,
      true,
    );
    onClose();
  }

  return (
    <>
      <Header title={title} subtitle={description} onBack={onBack} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Body>
          <FormField label="" htmlFor="" error={errors.text?.message}>
            <TextArea
              {...register("text")}
              hasError={!!errors.text?.message}
              autoFocus
              minRows={4}
              maxRows={6}
              placeholder="Write something..."
            />
          </FormField>
        </Body>

        <Footer className="justify-end">
          <Button variant="secondary-black" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" disabled={!selectedLeads.length}>
            Save
          </Button>
        </Footer>
      </form>
    </>
  );
}
